import { Grid, Tooltip } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { BSFormDialog } from "../../../../../../../../../components/dialog/BSFormDialog"
import { FormNumberInput } from "../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { FormSelectInput } from "../../../../../../../../../components/inputs/select-input/FormSelectInput"
import { FormTextInput } from "../../../../../../../../../components/inputs/text-input/FormTextInput"
import { SuccessContext } from "../../../../../../../../../components/layout/success-snackbar"
import { BSParking } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"
import { BSParkingCreationDto } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParkingCreationDto"
import { BSParkingUpdateDto } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParkingUpdateDto"
import { ParkingEnum, parkingEnumLabels } from "../../../../../../../../../core/enum/beem-shot/ParkingEnum"
import { useForm } from "../../../../../../../../../core/hooks/form/use-form"
import { nonZero, required } from "../../../../../../../../../core/hooks/form/validation"
import { enumToSelectOptions } from "../../../../../../../../../core/services/helper-service"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"
import { BSParkingContext } from "../context/BSParkingContext"
import { BSParkingListContext } from "../context/BSParkingListContext"

export interface IForm {
  name: string
  typeParking: ParkingEnum
  nombrePlace: number
  nombreAscenseur: number
  nombreEscalier: number
}

export function bsParkingFormToCreationDto(form: IForm, bsBuildingId: string): BSParkingCreationDto {
  return {
    bsBuildingId,
    name: form.name,
    typeParking: form.typeParking,
    nombrePlace: form.nombrePlace,
    nombreAscenseur: form.nombreAscenseur,
    nombreEscalier: form.nombreEscalier,
  }
}
export function bsParkingFormUpdateToDto(form: IForm, bsParkingId: string): BSParkingUpdateDto {
  return {
    id: bsParkingId,
    name: form.name,
    typeParking: form.typeParking,
    nombrePlace: form.nombrePlace,
    nombreAscenseur: form.nombreAscenseur,
    nombreEscalier: form.nombreEscalier,
  }
}

export function bsParkingDtoToForm(dto: BSParking | undefined): IForm {
  return dto
    ? {
        name: dto.name,
        typeParking: dto.typeParking,
        nombrePlace: dto.nombrePlace,
        nombreAscenseur: dto.nombreAscenseur,
        nombreEscalier: dto.nombreEscalier,
      }
    : ({
        name: "",
        typeParking: ParkingEnum.SUPER_STRUCTURE,
        nombrePlace: 0,
        nombreAscenseur: 0,
        nombreEscalier: 0,
      } as IForm)
}

interface IProps {
  isOpen: boolean
  isUpdateMode?: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  isSuperstructure?: boolean
}

export function BSParkingFormDialog({
  isUpdateMode,
  isOpen,
  setIsOpen,
  isSuperstructure,
}: Readonly<IProps>): React.JSX.Element {
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { selectedBSParking, setSelectedBSParking, createBSParking, updateBSParking } = useContext(BSParkingContext)
  const openSuccessSnackbar = useContext(SuccessContext)
  const { bsParkingList } = useContext(BSParkingListContext)

  const handleClose: () => void = useCallback(() => {
    setIsOpen(false)
    setSelectedBSParking(undefined)
  }, [setIsOpen, setSelectedBSParking])

  const submit: (form: IForm) => Promise<any> = useCallback(
    (form) => {
      let typeParking = form.typeParking

      if (isSuperstructure !== undefined) {
        typeParking = isSuperstructure ? ParkingEnum.SUPER_STRUCTURE : ParkingEnum.SOUTERRAIN
      }

      const finalForm: IForm = {
        ...form,
        typeParking,
      }

      if (!isUpdateMode && selectedBSBuilding) {
        return createBSParking(bsParkingFormToCreationDto(finalForm, selectedBSBuilding.id)).then(() => {
          openSuccessSnackbar("Le parking vient d'être créé.")
          handleClose()
        })
      } else if (isUpdateMode && selectedBSParking) {
        return updateBSParking(bsParkingFormUpdateToDto(finalForm, selectedBSParking.id)).then(() => {
          handleClose()
          openSuccessSnackbar("Les informations du parking ont été mises à jour.")
        })
      }
      return Promise.resolve()
    },
    [
      isSuperstructure,
      isUpdateMode,
      selectedBSBuilding,
      selectedBSParking,
      createBSParking,
      openSuccessSnackbar,
      handleClose,
      updateBSParking,
    ]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting, resetForm } = useForm(
    selectedBSParking,
    bsParkingDtoToForm,
    [required("name"), required("typeParking"), nonZero("nombrePlace")],
    submit
  )

  const dialogTitle = useMemo(() => {
    if (isSuperstructure === undefined) {
      return isUpdateMode ? "Mettre à jour les informations du parking" : "Renseigner les informations du parking"
    } else {
      return "Vous devez créer un parking"
    }
  }, [isSuperstructure, isUpdateMode])

  /** temporary forbid to add more than one parking EXTERIEUR,
   * because it is handle in BSInput to generate the necessary BS Items */
  const [hasParkingExterieur, setHasParkingExterieur] = useState<boolean>(false)

  useEffect(() => {
    if (bsParkingList.some((parking) => parking.typeParking === ParkingEnum.EXTERIEUR)) {
      setHasParkingExterieur(true)
    } else {
      setHasParkingExterieur(false)
    }
  }, [bsParkingList])

  const options = useMemo(() => {
    if (hasParkingExterieur && form.typeParking !== ParkingEnum.EXTERIEUR) {
      return enumToSelectOptions(ParkingEnum, parkingEnumLabels).filter(
        (parking) => parking.value !== ParkingEnum.EXTERIEUR && parking.value !== ParkingEnum.SANS
      )
    } else {
      return enumToSelectOptions(ParkingEnum, parkingEnumLabels).filter((parking) => parking.value !== ParkingEnum.SANS)
    }
  }, [form.typeParking, hasParkingExterieur])
  //end.

  return (
    <BSFormDialog
      title={dialogTitle}
      open={isOpen}
      formId={isUpdateMode ? "update-bs-building-parking" : "create-bs-building-parking"}
      formContent={
        <>
          <Grid item xs={12}>
            <FormTextInput fieldName="name" label="Nom du parking" form={form} handleChange={handleChange} errors={errors} />
          </Grid>

          <Grid item xs={6}>
            {isSuperstructure !== undefined ? (
              <FormSelectInput
                id="typeParking"
                label="Type de parking"
                mode="event"
                selectedOption={isSuperstructure ? ParkingEnum.SUPER_STRUCTURE : ParkingEnum.SOUTERRAIN}
                options={options}
                handleEventChange={handleChange}
                errors={errors}
                disabled
              />
            ) : (
              <Tooltip
                title={selectedBSParking?.assigned ? "Vous ne pouvez pas changer le type d'un parking affecté" : undefined}
                arrow
                placement="right">
                <span>
                  <FormSelectInput
                    id="typeParking"
                    label="Type de parking"
                    mode="event"
                    selectedOption={form.typeParking}
                    options={options}
                    handleEventChange={handleChange}
                    errors={errors}
                    disabled={selectedBSParking?.assigned}
                  />
                </span>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={6}>
            <FormNumberInput
              fieldName="nombrePlace"
              label="Nombre de places"
              handleEventChange={handleChange}
              form={form}
              errors={errors}
            />
          </Grid>

          {form.typeParking !== ParkingEnum.EXTERIEUR && (
            <>
              <Tooltip title="Ajoutez les ascenseurs liés à l’usage sélectionné" arrow>
                <Grid item xs={6}>
                  <FormNumberInput
                    fieldName="nombreAscenseur"
                    label="Nombre de cages d'ascenseurs"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
              </Tooltip>
              <Tooltip title="Ajoutez les escaliers liés à l’usage sélectionné" arrow>
                <Grid item xs={6}>
                  <FormNumberInput
                    fieldName="nombreEscalier"
                    label="Nombre de cages d'escaliers"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
              </Tooltip>
            </>
          )}
        </>
      }
      isSubmitting={isSubmitting}
      onClose={() => {
        handleClose()
        resetForm()
      }}
      onSubmit={handleSubmit}
    />
  )
}
